import { useGenericModal } from "src/components/reusable/components/GenericModal";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";

export function useCheckoutErrorHandler() {
  const teamConfig = useTeamConfig();
  const errorModal = useGenericModal();

  const ADD_ON_CHECKOUT_202_ERROR_MESSAGE = teamConfig?.LANGUAGES
    ?.ADD_ON_CHECKOUT_202_ERROR_MESSAGE || (
    <>
      We’re currently experiencing high order volume, and your order is still
      processing. If your card has been charged, your tickets will be available
      in your Ticketmaster account once they are ready to manage.
      <br />
      <br />
      Please allow up to one hour for your order to appear in your purchase
      history.
      <br />
      <br />
      <b>
        Do not attempt to place the order again during this time to avoid
        duplicate charges.
      </b>
    </>
  );

  const PASS_CHECKOUT_202_ERROR_MESSAGE = teamConfig?.LANGUAGES
    ?.PASS_CHECKOUT_202_ERROR_MESSAGE || (
    <>
      We’re currently experiencing high order volume, and your order is
      processing.
      <br />
      <br />
      If your card has been charged, your purchase will be reflected in your
      account within one hour. Once added, you’ll have access to manage your
      purchase.
      <br />
      <br />
      <b>
        Do not attempt to place the order again during this time to avoid
        duplicate charges.
      </b>
    </>
  );

  const fireCheckout202ErrorModal = (
    errorMessage: string | React.ReactNode
  ) => {
    return new Promise((resolve) => {
      const onClose = () => {
        errorModal.reset();
        resolve(true);
      };

      errorModal.set({
        isOpen: true,
        hideTitleIcon: true,
        title: "Order Status",
        description: errorMessage,
        isCloseButtonHidden: true,
        confirmButtonText: "Close",
        onClickConfirm: onClose,
        closeIconProps: { onClick: onClose },
      });
    });
  };

  const is202ErrorCode = (error: any) => {
    let is202 = false;
    is202 = error?.response?.data?.info === "202_ERROR";
    if (error?.code === "ECONNABORTED") is202 = true;
    return is202;
  };

  const fireDefaultErrorModal = (error: any) => {
    let errorMessage = "";
    if (typeof error == "string") errorMessage = error;
    else errorMessage = findApiErrorMessage(error);

    return new Promise((resolve) => {
      const onClose = () => {
        errorModal.reset();
        resolve(true);
      };

      errorModal.set({
        isOpen: true,
        hideTitleIcon: true,
        title: "Error",
        description: errorMessage,
        isCloseButtonHidden: true,
        confirmButtonText: "Close",
        onClickConfirm: onClose,
        closeIconProps: { onClick: onClose },
      });
    });
  };

  const handleAddOnCheckoutError = async (error: any) => {
    const is202Error = is202ErrorCode(error);
    if (is202Error) {
      await fireCheckout202ErrorModal(ADD_ON_CHECKOUT_202_ERROR_MESSAGE);
    } else {
      await fireDefaultErrorModal(error);
    }
    return { is202Error };
  };

  const handlePassCheckoutError = async (error: any) => {
    const is202Error = is202ErrorCode(error);
    if (is202Error) {
      await fireCheckout202ErrorModal(PASS_CHECKOUT_202_ERROR_MESSAGE);
    } else {
      await fireDefaultErrorModal(error);
    }
    return { is202Error };
  };

  return {
    handleAddOnCheckoutError,
    handlePassCheckoutError,
  };
}
